/**
 * PushToQuoteHealthSafety · iter303ak-fix26 — Quote Sent · T25 · A30
 * ───────────────────────────────────────────────────────────────
 * Auto-push acknowledgement card. On mount fires POST
 *   /appraisals/{id}/height-safety-line { estimate, types }
 * which inserts (or replaces) the H&S line on component_lines.
 * Then shows a receipt card and enables Continue.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Package, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const nz = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PushToQuoteHealthSafety({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const estimate = capturedData?.height_safety_estimate || {};
  const types = capturedData?.height_safety_types || [];
  const noCost = types.includes('none');

  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(value?.pushed || false);
  const [lineId, setLineId] = useState(value?.line_id || null);
  const [targetCount, setTargetCount] = useState(value?.target_ids?.length || 0);
  const [err, setErr] = useState('');

  const push = async () => {
    if (!appraisalId || pushing) return;
    setPushing(true); setErr('');
    try {
      const { data } = await api.post(`/appraisals/${appraisalId}/height-safety-line`, {
        estimate: noCost ? {} : estimate,
        types,
        none: noCost,
      });
      setPushed(true);
      setLineId(data?.inserted_id || null);
      setTargetCount((data?.target_ids || []).length);
      if (onChange) onChange({ pushed: true, line_id: data?.inserted_id, target_ids: data?.target_ids, pushed_at: new Date().toISOString(), total: data?.total });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to push line');
    } finally {
      setPushing(false);
    }
  };

  // Auto-push on mount if we have an estimate and haven't pushed yet.
  useEffect(() => {
    if (appraisalId && !pushed && (noCost || estimate?.grand_total > 0)) push();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appraisalId]);

  return (
    <div className="space-y-4" data-testid="wf-push-to-quote-health-safety">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-start gap-3">
          <Package className="w-5 h-5 mt-0.5 shrink-0 text-emerald-700" />
          <div>
            <div className="text-sm font-semibold text-emerald-900">Push Health & Safety Line to Quote</div>
            <div className="text-sm mt-1 text-emerald-800">
              {params.prompt || 'Auto-inserts a Safety line on the quote with the current estimate. This can be re-pushed if you edit the estimate.'}
            </div>
          </div>
        </div>
      </div>

      {!noCost && <div className="p-4 border border-slate-200 rounded-lg bg-white space-y-2">
        <div className="flex justify-between text-sm text-slate-700">
          <span>Types:</span>
          <span className="font-semibold">{types.join(' · ') || '—'}</span>
        </div>
        <div className="flex justify-between text-sm text-slate-700">
          <span>Perimeter × Storeys:</span>
          <span className="font-mono">{estimate.perimeter_m || 0}m × {estimate.storeys || 0}</span>
        </div>
        <div className="flex justify-between text-sm text-emerald-900 border-t border-slate-200 pt-2 font-semibold">
          <span>Estimate pushed as TBC:</span>
          <span className="font-mono text-base">{nz(estimate.grand_total)}</span>
        </div>
      </div>}

      {pushing && (
        <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-600 flex items-center gap-2">
          <RefreshCw size={12} className="animate-spin" /> Pushing to quote…
        </div>
      )}

      {pushed && noCost && (
        <div className="p-3 rounded-lg border border-emerald-300 bg-emerald-50 text-sm text-emerald-800 flex items-center gap-2" data-testid="wf-push-hs-none-receipt">
          <CheckCircle2 size={16} /> No safety cost — no Health & Safety line on the quote.
        </div>
      )}

      {pushed && !noCost && (
        <div className="p-3 rounded-lg border border-emerald-300 bg-emerald-50 text-sm text-emerald-800 flex items-center gap-2" data-testid="wf-push-hs-receipt">
          <CheckCircle2 size={16} /> <span data-testid="wf-hs-pushed-count">Line added to {targetCount || 1} quote{targetCount === 1 ? "" : "s"}.</span> Row id: <code className="text-[10px]">{lineId?.slice(-8)}</code>
        </div>
      )}

      {err && (
        <div className="p-3 rounded-lg border border-rose-300 bg-rose-50 text-sm text-rose-800 flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5" />
          <div className="flex-1">
            <div>Failed to push: {err}</div>
            <button
              type="button"
              onClick={push}
              className="mt-2 px-2 py-1 rounded-md text-xs font-semibold text-rose-700 bg-white border border-rose-300 hover:bg-rose-50 inline-flex items-center gap-1"
            >
              <RefreshCw size={11} /> Retry
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-push-hs-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-push-hs-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={!pushed || pushing}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-push-hs-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
